/**
 * judgeContent.js - the game's half of the judgement.
 *
 * PURE. Frames in, verdicts out.
 *
 * ---------------------------------------------------------------------------
 * THE DIVISION, FOR THE THREE PHASES THIS GAME COVERS
 *
 *   Phase 1  A  Attends to Model      nobody   observational, permanent
 *            B  Attempts Vocalization engine   from the audio envelope
 *            C  Produces Target       GAME     here
 *
 *   Phase 2  A  Responds to Prompt    engine   from the ladder walk
 *            B  Retrieves Word        engine
 *            C  Produces Independently engine
 *                                     ...the game judges NOTHING at phase 2.
 *
 *   Phase 3  A  Carrier Phrase        GAME
 *            B  Object Label          GAME
 *            C  Combination           GAME
 *            D  Spontaneous           GAME
 *
 * A game must send exactly the boxes it owns and no others: scoring.js refuses
 * an attempt that carries a box the engine judges, or misses one it does not
 * (section 8.1 rule 3). judgeAttempt() returns that exact set.
 *
 * ---------------------------------------------------------------------------
 * SUCCESS IS NOT THE SAME THING AS A BOX
 *
 * `success` is whether the bubble pops - what the CHILD experiences. The boxes
 * are what the chart records. They are computed from the same utterance and
 * they are not the same question, and conflating them is how a chart ends up
 * with one boolean copied into four columns.
 *
 * At Phase 3 the bubble pops on C (the combination), not on B (the noun). A
 * child who says only "apple" has scored B and not C, and the game must not
 * pop the bubble for it - if it did, the child would learn that the second
 * word is decoration. That is audit finding B43 stated as a design rule.
 */

import { trialShape } from '../../../../engine/judgement.js';
import {
  acceptedPhrases,
  canonicalPhrase,
  getObject,
  getCarrier,
  CARRIER_IDS,
} from '../content/objects.js';
import { producedAny, orderedCombination } from '../../shared/phrase.js';

/**
 * @typedef {{objectId:string, carrierId?:string|null}} Target
 */

/**
 * @param {object} input
 * @param {number} input.phase
 * @param {Target} input.target  what Pip asked for. At phase 3 this is only a
 *        SUGGESTION - see judgePhase3.
 * @param {{objects:string[], carriers?:string[]}|null} [input.choices] what is
 *        on screen and therefore askable. Phase 3 only.
 * @param {object|null} input.trial   NORMALISED trial record (engine/trial.js).
 *        Required at phase 3, where component D asks whether the child spoke
 *        before Pip modelled. Not read at phases 1 and 2.
 * @param {string|null} [input.transcript]
 * @param {Array} [input.alternatives]
 * @returns {{components:Object<string,object>, success:boolean,
 *            expected:string, heard:string|null, produced:boolean,
 *            detail:object}}
 */
export function judgeAttempt({
  phase,
  target,
  choices = null,
  trial = null,
  transcript = null,
  alternatives = [],
}) {
  const expected = canonicalPhrase(target);

  if (phase === 1) return judgePhase1({ target, expected, transcript, alternatives });
  if (phase === 2) return judgePhase2({ target, expected, transcript, alternatives });
  if (phase === 3) return judgePhase3({ target, expected, choices, trial, transcript, alternatives });

  throw new Error(
    `Bubble Pop covers phases 1-3; asked to judge phase ${phase}. `
    + 'A game must never guess at a phase it does not implement - it would '
    + 'write a box that means something different from every other game.'
  );
}

/* ---------------------------------------------------------------------------
 * PHASE 1 - one box, and it is the plainest one in the framework.
 * ------------------------------------------------------------------------- */
function judgePhase1({ target, expected, transcript, alternatives }) {
  const hit = producedAny({
    phase: 1,
    targets: acceptedPhrases(target),
    transcript,
    alternatives,
  });

  return {
    components: {
      C: {
        success: hit.produced,
        expected,
        heard: hit.matched,
        reason: hit.reason,
        // Which of the accepted variants matched, so a run of "son" for "sun"
        // is visible in the log rather than looking like a clean success.
        matchedVariant: hit.produced ? hit.target : null,
        matchedIn: hit.matchedIn,
      },
    },
    success: hit.produced,
    produced: hit.produced,
    expected,
    heard: hit.matched,
    detail: { rule: hit.rule },
  };
}

/* ---------------------------------------------------------------------------
 * PHASE 2 - the game judges nothing and still has to know what happened.
 *
 * All three boxes belong to the engine, which reads them off the ladder walk.
 * The game still needs `success` to decide whether the bubble pops, so it runs
 * the same match for its own purposes and submits no components at all.
 * ------------------------------------------------------------------------- */
function judgePhase2({ target, expected, transcript, alternatives }) {
  const hit = producedAny({
    phase: 2,
    targets: acceptedPhrases(target),
    transcript,
    alternatives,
  });

  return {
    // Empty on purpose. POST /api/engine/attempts fills A, B and C from the
    // trial record and would REFUSE this attempt if the game asserted them.
    components: {},
    success: hit.produced,
    produced: hit.produced,
    expected,
    heard: hit.matched,
    detail: { rule: hit.rule, judgedBy: 'engine', reason: hit.reason },
  };
}

/* ---------------------------------------------------------------------------
 * PHASE 3 - four boxes, and they genuinely separate.
 *
 *   A  the action word is there            "want ..."
 *   B  the object word is there            "... apple"
 *   C  both, in that order, in one hearing "want apple", not "apple want"
 *   D  said before Pip gave the model      not an echo
 *
 * A and B can both pass while C fails; C can pass while D fails. Only D
 * depends on the trial record.
 *
 * ---------------------------------------------------------------------------
 * THE CHILD CHOOSES, NOT PIP
 *
 * Phase 3 is MANDING: "Child says 'want apple' -> gets the apple" (section
 * 4.3). A mand is a request, and a request the child did not choose is not a
 * request - it is a recitation. So every object on screen is a legal answer and
 * every carrier in the framework's closed set {want, put, get, more} is a legal
 * verb, whatever the level happens to be offering on screen. The game resolves
 * what the child actually asked for and then does that thing.
 *
 * The level's own target survives as Pip's SUGGESTION: it is what Pip models at
 * the full-model rung, and it is what goes in the trial record's targetWord,
 * because that field holds exactly one string. Recording one expected answer
 * for a phase whose whole point is that there are several is a real limitation
 * - docs/audit/GAME-01.md section 7.3.
 * ------------------------------------------------------------------------- */

/**
 * Which request did the child actually make?
 *
 * Scans every (carrier, object) pair that is currently askable and keeps the
 * best evidence found, ranked: a correctly ordered pair beats both-words-
 * unordered, which beats a bare noun, which beats a bare carrier.
 *
 * @returns {{objectId:string|null, carrierId:string|null, combo:object, rank:number}}
 */
export function resolveRequest({ objects, carriers, transcript, alternatives }) {
  let best = { objectId: null, carrierId: null, combo: null, rank: -1 };

  for (const carrierId of carriers) {
    const carrier = getCarrier(carrierId);
    if (!carrier) continue;

    for (const objectId of objects) {
      const object = getObject(objectId);
      if (!object) continue;

      const combo = orderedCombination({
        phase: 3,
        carrierWords: carrier.accept,
        nounWords: object.accept,
        transcript,
        alternatives,
      });

      const rank = combo.ordered ? 3
        : (combo.carrier && combo.noun) ? 2
        : combo.noun ? 1
        : combo.carrier ? 0
        : -1;

      if (rank > best.rank) best = { objectId, carrierId, combo, rank };
      if (rank === 3) return best;
    }
  }

  return best;
}

function judgePhase3({ target, expected, choices, trial, transcript, alternatives }) {
  const suggested = getObject(target.objectId);
  if (!suggested) throw new Error(`Phase 3 target needs an object; got ${JSON.stringify(target)}.`);

  const objects = (choices && choices.objects && choices.objects.length)
    ? choices.objects
    : [target.objectId];
  // Section 6.4 gives the carrier set as a closed set, not a per-level list.
  // A child who says "put apple" on a level offering "want" has still used a
  // carrier phrase correctly, and the game should do what they asked.
  const carriers = (choices && choices.carriers && choices.carriers.length)
    ? choices.carriers
    : CARRIER_IDS;

  const resolved = resolveRequest({ objects, carriers, transcript, alternatives });
  const combo = resolved.combo || {
    carrier: false, noun: false, ordered: false, candidate: transcript || null,
    carrierWord: null, nounWord: null, reason: 'Nothing on screen was asked for.',
  };

  const object = getObject(resolved.objectId) || suggested;
  const carrier = getCarrier(resolved.carrierId) || getCarrier(target.carrierId) || getCarrier(carriers[0]);
  const asked = combo.ordered ? canonicalPhrase({ objectId: object.id, carrierId: carrier.id }) : expected;

  // COMPONENT-JUDGEMENT.md section 5, phase 3 D: "no model of this phrase
  // within the last N seconds", with the confound named - on a trial where Pip
  // never modelled, D is free. This game's ladder always ENDS at the full
  // model, so a model was always available; whether it had played before the
  // child spoke is the actual measurement. `modelWasAvailable` is carried in
  // the detail so a therapist can see which trials the box could discriminate
  // on. The engine has no way to leave a box unwritten for one trial - see
  // docs/audit/GAME-01.md section 7.4.
  const shape = trial ? trialShape(trial) : null;
  const echoed = shape ? shape.fullModelBeforeOnset : false;

  return {
    components: {
      A: {
        success: combo.carrier,
        expected: carrier.word,
        heard: combo.carrierWord,
        reason: combo.carrier
          ? `Used the action word "${combo.carrierWord}".`
          : `No action word. The framework's set is ${CARRIER_IDS.join(', ')}.`,
      },
      B: {
        success: combo.noun,
        expected: object.word,
        heard: combo.nounWord,
        reason: combo.noun
          ? `Named "${combo.nounWord}".`
          : 'No object on screen was named.',
      },
      C: {
        success: combo.ordered,
        expected: asked,
        heard: combo.candidate,
        reason: combo.reason,
      },
      D: {
        // WINDOW: this trial. The framework's phase 3 component D note records
        // why, and the engine - not this game - decides whether the trial
        // could answer the question at all (CONDITIONALS.model_opportunity).
        success: combo.ordered && !echoed,
        expected: asked,
        heard: combo.candidate,
        reason: !combo.ordered
          ? 'No two-word request to credit.'
          : (echoed
              ? 'Pip had already said the whole phrase before the child spoke, '
                + 'so this is an echo rather than a request of their own.'
              : 'Requested it before Pip modelled the phrase.'),
        modelWasAvailable: Boolean(trial && (trial.modelScheduled || trial.modelPlayed)),
        // Evidence, beside the box and not folded into it: a child modelled
        // eight seconds ago on the PREVIOUS trial may still be echoing, and
        // the trial-window reading cannot see that. Recorded so it can be
        // looked at; never scored here.
        priorModelAgoMs: trial ? trial.priorModelAgoMs : null,
      },
    },
    success: combo.ordered,
    produced: combo.noun,
    expected: asked,
    heard: combo.candidate,
    detail: {
      // What the child asked for, which may not be what Pip suggested. The
      // game acts this out; `effect` is why the second word is worth saying.
      objectId: combo.ordered || combo.noun ? object.id : null,
      carrier: carrier.id,
      effect: carrier.effect,
      suggestedObjectId: suggested.id,
      choseTheirOwn: combo.ordered && object.id !== suggested.id,
      echoed,
      partial: combo.noun && !combo.ordered,
    },
  };
}

/**
 * The component codes this game submits for a level, so the caller can check
 * itself against framework.getAssessableBoxes() before it posts.
 * @param {number} phase
 * @returns {string[]}
 */
export function gameJudgedCodes(phase) {
  if (phase === 1) return ['C'];
  if (phase === 2) return [];
  if (phase === 3) return ['A', 'B', 'C', 'D'];
  return [];
}

export default { judgeAttempt, gameJudgedCodes };
